import { Box, Icon, Stack, TableRow, Tooltip, Typography } from '@mui/material';
import { ParentSearchResult, StudentWithParents, useGetParentsQuery } from '@schooly/api';
import { SchoolRelation } from '@schooly/api';
import { SORT_DIRECTION } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getUserTypeTextId } from '../../../helpers/misc';

import { SearchInput } from '@schooly/components/filters';
import {
  ArrowRightIcon,
  AttentionIcon,
  Grid,
  GridBody,
  GridCell,
  GridHead,
  SkeletonGridLoader,
  SkeletonRows,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import FamilyMembers from '../FamilyMembers';
import { getSchoolUserId } from 'apps/web/src/helpers/school';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { NoSearchResultsFound } from '../NoSearchResultsFound/NoSearchResultsFound';

interface AddParentsHomeProps {
  onUserSelect: (user: SchoolRelation) => void;
}

const AddParentsHome: React.FC<AddParentsHomeProps> = ({ onUserSelect }) => {
  const { schoolId = '' } = useAuth();
  const { formatMessage } = useIntl();

  const { data, hasNextPage, isFetchingNextPage, isLoading, params, setParams, fetchNextPage } =
    useGetParentsQuery(
      {
        query: '',
        schoolId,
        filters: {},
        sort: { columnTextId: 'edit_access', direction: SORT_DIRECTION.DESC },
        additionalSortOptions: [{ columnTextId: 'students', direction: SORT_DIRECTION.ASC }],
      },
      { refetchOnMount: 'always', enabled: !!schoolId },
    );

  const handleSetQuery = useCallback(
    (query: string) => setParams((p) => ({ ...p, query })),
    [setParams],
  );

  const { parents, editableStudentsWithParents, notEditableStudentsWithParents } = useMemo(() => {
    const parents =
      data?.pages.reduce<ParentSearchResult[]>((prev, curr) => [...prev, ...curr.results], []) ??
      [];

    const allStudentsWithParents = parents.reduce<StudentWithParents[]>((acc, parent) => {
      const prevStudent = acc.length > 0 ? acc[acc.length - 1] : undefined;

      if ('student' in parent) {
        const student = parent.student as StudentWithParents;

        if (!prevStudent || prevStudent.relation_id !== student.relation_id) {
          acc.push({ ...student, parents: [parent] });
        } else {
          if (!prevStudent.parents.map((p) => p.relation_id).includes(parent.relation_id))
            prevStudent.parents.push(parent);
        }
      }

      return acc;
    }, []);

    const firstNotEditableStudentIndex = allStudentsWithParents.findIndex(
      (s) => s.edit_access === false,
    );

    return {
      parents,
      allStudentsWithParents,
      editableStudentsWithParents:
        firstNotEditableStudentIndex !== -1
          ? allStudentsWithParents.slice(0, firstNotEditableStudentIndex)
          : allStudentsWithParents,
      notEditableStudentsWithParents:
        firstNotEditableStudentIndex !== -1
          ? allStudentsWithParents.slice(firstNotEditableStudentIndex)
          : [],
    };
  }, [data]);

  const renderContent = () => {
    if (!isLoading && !!params.query && !parents.length) return <NoSearchResultsFound />;

    return (
      <Grid stickyHeader>
        <GridHead borderBottom>
          <GridCell width="50%">{formatMessage({ id: 'people-Name' })}</GridCell>
          <GridCell width="50%">{formatMessage({ id: 'userType-parent-plural' })}</GridCell>
        </GridHead>
        <GridBody>
          {editableStudentsWithParents.map((student, i) => (
            <StudentWithParentsRow
              student={student}
              onClick={() => onUserSelect(student)}
              key={(getSchoolUserId(student) ?? '') + i}
            />
          ))}
          {!!notEditableStudentsWithParents.length && (
            <TableRow component={Stack}>
              <Typography sx={{ pt: 3 }} color="text.secondary" variant="h4">
                {formatMessage({ id: 'parents-NoAccess' })}
              </Typography>
            </TableRow>
          )}
          {notEditableStudentsWithParents.map((student, i) => (
            <StudentWithParentsRow student={student} key={(getSchoolUserId(student) ?? '') + i} />
          ))}
          {isLoading && <SkeletonRows columnsCount={2} amount={10} />}
          <SkeletonGridLoader
            isFetching={isLoading || isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            columnsCount={2}
            amount={10}
          />
        </GridBody>
      </Grid>
    );
  };

  return (
    <Stack height="100%" gap={1}>
      <SearchInput
        onChangeText={handleSetQuery}
        debounced
        value={params.query ?? ''}
        placeholder={formatMessage(
          { id: 'people-SearchAmongTypeCount' },
          {
            userTypePlural: formatMessage({
              id: getUserTypeTextId('student', true),
            }).toLowerCase(),
          },
        )}
      />
      <Stack
        sx={(theme) => ({
          flex: 1,
          overflowY: 'scroll',
          mx: theme.spacing(-2),
          px: theme.spacing(2),
        })}
      >
        {renderContent()}
      </Stack>
    </Stack>
  );
};

const StudentWithParentsRow: FC<{ student: StudentWithParents; onClick?: () => void }> = ({
  student,
  onClick,
}) => {
  const isDisabled = !student.edit_access;
  const hint = student.edit_access ? null : <FormattedMessage id="parents-NoEditAccessHint" />;

  return (
    <TableRow
      component={Stack}
      onClick={!isDisabled ? onClick : undefined}
      sx={(theme) => ({
        cursor: !isDisabled ? 'pointer' : undefined,
        '.arrow-right-icon': {
          opacity: 0,
        },
        '&:hover td': {
          backgroundColor: !isDisabled ? theme.palette.background.default : undefined,
          '.arrow-right-icon': {
            opacity: 1,
          },
        },
      })}
    >
      <GridCell pl={6} sx={{ position: 'relative' }}>
        <Box
          sx={(theme) => ({
            position: 'absolute',
            left: theme.spacing(1),
            marginTop: theme.spacing(-0.5),
          })}
        >
          <AvatarAuth user={student} withAvatarPreview />
        </Box>

        <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
          {getUserFullName(student)}{' '}
          {!!hint && (
            <Tooltip disableInteractive title={hint}>
              <Typography variant="h3" component={'span'}>
                <AttentionIcon />
              </Typography>
            </Tooltip>
          )}
        </TypographyWithOverflowHint>
      </GridCell>
      <GridCell sx={{ position: 'relative' }}>
        <Typography
          sx={{
            pr: !isDisabled ? 3 : undefined,
            '.PeopleList-item-extra-name': {
              pr: 0.75,
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          }}
        >
          <FamilyMembers user={student} userType="student" studentWithParents />
        </Typography>
        {!isDisabled && (
          <Icon
            className="arrow-right-icon"
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              right: 8,
              top: 'calc(50% - 8px)',
              height: 16,
            }}
          >
            <ArrowRightIcon />
          </Icon>
        )}
      </GridCell>
    </TableRow>
  );
};

export default AddParentsHome;
